import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import { api } from '../context/AuthContext';
import { useSEO } from '../hooks/useSEO';

const RegionsPage = () => {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: 'Regioni',
    description: 'Esplora i prodotti artigianali e alimentari per regione del Sud Italia: Campania, Calabria, Sicilia, Puglia, Basilicata e Sardegna.',
    canonicalPath: '/regioni',
  });

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await api.get('/catalog/regions');
        setRegions(res.data);
      } catch (error) {
        console.error('Error fetching regions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRegions();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 rounded w-1/3 mb-8" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-200 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="regions-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
            Regioni del Sud Italia
          </h1>
          <p className="mt-2 text-text-secondary">
            Esplora i sapori autentici di ogni territorio
          </p>
        </div>

        {/* Regions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {regions.map(region => (
            <Link
              key={region.id}
              to={`/prodotti?region=${region.id}`}
              className="group relative h-64 rounded-xl overflow-hidden"
              data-testid={`region-card-${region.slug}`}
            >
              <img
                src={region.image_url || 'https://images.pexels.com/photos/2927972/pexels-photo-2927972.jpeg'}
                alt={region.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
                  <MapPin className="w-4 h-4" />
                  <span>Sud Italia</span>
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-2">
                  {region.name}
                </h3>
                {region.description && (
                  <p className="text-white/90 text-sm line-clamp-2 mb-3">
                    {region.description}
                  </p>
                )}
                <span className="inline-flex items-center gap-1 text-white font-medium text-sm group-hover:underline">
                  Scopri i prodotti
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {regions.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl">
            <MapPin className="w-16 h-16 mx-auto text-text-muted mb-4" />
            <p className="text-text-secondary">Nessuna regione disponibile</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegionsPage;
